const SERIF = "Georgia, 'Times New Roman', serif";
const SANS = "Helvetica, Arial, sans-serif";

export type ResourceItem = { name: string; detail: string };

/**
 * Town-level coverage detail for local SEO pages: named neighborhoods and ZIP
 * codes, county resources, and hospital/detox referral context.
 *
 * Every page passes its own copy — nothing in here is templated text.
 */
const LocalCoverageSection = ({
  town,
  county,
  neighborhoodsHeading,
  neighborhoods,
  zips,
  neighborhoodNote,
  resourcesHeading,
  resources,
  referralHeading,
  referralBody,
}: {
  town: string;
  county: string;
  neighborhoodsHeading?: string;
  neighborhoods: string[];
  zips: string[];
  neighborhoodNote: React.ReactNode;
  resourcesHeading?: string;
  resources: ResourceItem[];
  referralHeading?: string;
  referralBody: React.ReactNode;
}) => (
  <section style={{ background: "#FAF4F0", padding: "60px 20px" }}>
    <div className="mx-auto" style={{ maxWidth: 1100 }}>
      <h2 style={{ fontFamily: SERIF, color: "#8B1A1A", fontSize: 30, margin: 0, lineHeight: 1.2 }}>
        {neighborhoodsHeading ?? `Neighborhoods and ZIP codes we serve in ${town}`}
      </h2>
      <p
        style={{
          fontFamily: SANS,
          color: "#2C2C2C",
          fontSize: 16,
          lineHeight: 1.7,
          marginTop: 16,
          marginBottom: 20,
        }}
      >
        {neighborhoodNote}
      </p>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
        {neighborhoods.map((n) => (
          <span
            key={n}
            style={{
              fontFamily: SANS,
              background: "#FFFFFF",
              border: "1px solid #F0E0E0",
              borderRadius: 999,
              color: "#6B1414",
              fontSize: 14,
              padding: "6px 14px",
            }}
          >
            {n}
          </span>
        ))}
      </div>
      <p style={{ fontFamily: SANS, color: "#555", fontSize: 14, margin: "0 0 36px" }}>
        <strong>ZIP codes:</strong> {zips.join(" · ")}
      </p>

      <h2 style={{ fontFamily: SERIF, color: "#8B1A1A", fontSize: 26, margin: "0 0 16px", lineHeight: 1.25 }}>
        {resourcesHeading ?? `${county} resources worth knowing about`}
      </h2>
      <ul style={{ listStyle: "none", padding: 0, margin: "0 0 36px", display: "grid", gap: 12 }}>
        {resources.map((r) => (
          <li
            key={r.name}
            style={{
              background: "#FFFFFF",
              borderLeft: "4px solid #8B1A1A",
              borderRadius: 8,
              padding: "14px 16px",
              fontFamily: SANS,
              color: "#2C2C2C",
              fontSize: 15,
              lineHeight: 1.65,
            }}
          >
            <strong style={{ color: "#6B1414" }}>{r.name}</strong> — {r.detail}
          </li>
        ))}
      </ul>

      <h2 style={{ fontFamily: SERIF, color: "#8B1A1A", fontSize: 26, margin: "0 0 16px", lineHeight: 1.25 }}>
        {referralHeading ?? `Detox and hospital coordination for ${town} patients`}
      </h2>
      <div style={{ fontFamily: SANS, color: "#2C2C2C", fontSize: 16, lineHeight: 1.75 }}>{referralBody}</div>
    </div>
  </section>
);

export default LocalCoverageSection;
